/* ==========================================================================
   BOUNTEOUS CONFIDENTIAL
   ___________________

   Copyright 2019 Bounteous
   All Rights Reserved.

   NOTICE:  All information contained herein is, and remains the property
   of Bounteous and its suppliers, if any. The intellectual and
   technical concepts contained herein are proprietary to Bounteous
   and its suppliers and are protected by trade secret or copyright law.
   Dissemination of this information or reproduction of this material
   is strictly forbidden unless prior written permission is obtained
   from Bounteous.
   ========================================================================== */

import { Component, registerComponent } from 'js/component.js';
import { isEditModeFooter, throttle } from 'js/helpers.js';
import { getBreakpointMediaQuery } from 'js/constants.js';

const componentName = 'cmp-iframe';

export class Iframe extends Component {

    /**
     * Get Data From Data-Attrs
     */
    getInitialData() {
        if(!this.elements.container) return;
        if(!this.elements.container.dataset) return;

        const {
            mobileWidth,
            mobileHeight,
            tabletWidth,
            tabletHeight,
            desktopWidth,
            desktopHeight,
        } = this.elements.container.dataset;

        this.mobileWidth = mobileWidth || null;
        this.mobileHeight = mobileHeight || null;
        this.tabletWidth = tabletWidth || null;
        this.tabletHeight = tabletHeight || null;
        this.desktopWidth = desktopWidth || null;
        this.desktopHeight = desktopHeight || null;
    }

    /**
     * Update Iframe Size
     */
    updateIframe() {
        // Set our maxWidth/height based on current breakpoint
        // [LEGACY BREAKPOINT: max-width 767px -> getBreakpointMediaQuery('down', 'x-small')]
        if (getBreakpointMediaQuery('down', 'x-small').matches) {
            this.maxWidth = this.mobileWidth;
            this.height = this.mobileHeight;
        // [LEGACY BREAKPOINT: max-width 1024px -> getBreakpointMediaQuery('down', 'medium')]
        } else if (getBreakpointMediaQuery('down', 'medium').matches) {
            this.maxWidth = this.tabletWidth;
            this.height = this.tabletHeight;
        } else {
            this.maxWidth = this.desktopWidth;
            this.height = this.desktopHeight;
        }

        // Update noheight class & add height if available
        if(!this.elements.container) return;
        if (this.height !== null && this.height !== '') {
            this.elements.container.classList.remove('noheight');
            this.element.style.height = `${this.height}px`;
        } else {
            this.elements.container.classList.add('noheight');
        }

        // Update iframe width
        this.element.style.maxWidth = this.maxWidth.indexOf('%') > -1 ? `${this.maxWidth}` : `${this.maxWidth}px`;

        // Update edit mode iframe
        this.updateIframeEditMode();
    }

    /**
     * Update Iframe Edit Mode
     */
    updateIframeEditMode() {
        if(!isEditModeFooter()) return;

        // Show the iframeContainer to ensure proper height measurement.
        this.elements.container.style.display = 'block';
        this.elements.editMessage.style.display = 'none';

        // Re-fetching height/width to get the actual on-screen values taking all styling into consideration.
        const editHeight = this.element.getBoundingClientRect().height;
        const editWidth = this.element.getBoundingClientRect().width;

        this.elements.editWidth.innerHTML = editWidth;
        this.elements.editHeight.innerHTML = editHeight;

        if (editHeight < 25 || editWidth < 25) {
            this.elements.editMessage.style.display = 'block';
            this.elements.container.style.display = 'none';
            this.element.style.height = '0px';
            this.element.style.maxWidth = '100%';
        }
    }

    allowMultipleTids() {
        const iframe = this.elements?.iframe;
        const src = iframe?.getAttribute?.('src') ?? iframe?.getAttribute?.('data-src');
        const tid = src?.split('?tid=')[1];
        const link = document.querySelector('.cmp-product-variant-grid__launch-assistant-desktop a');
        if (tid && !tid.includes(',') && link) {
            link.href = `https://configurator.rockwellautomation.com/#/configure/template/${tid}`;
        }
    }

    /**
     * Add Event Listeners
     */
    addEventListeners() {
        window.addEventListener('resize', throttle(() => {
            this.updateIframe();
        }));
    }

    /**
     * Initialize Component
     */
    init() {
        // Set Up Data
        this.windowWidth = () => Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
        this.maxWidth = null;
        this.height = null;
        this.mobileWidth = null;
        this.mobileHeight = null;
        this.tabletWidth = null;
        this.tabletHeight = null;
        this.desktopWidth = null;
        this.desktopHeight = null;

        // Set Up Elements
        this.elements = {
            container: this.element.querySelector(`.${componentName}__container`),
            editMessage: this.element.querySelector(`.${componentName}__small-edit`),
            editWidth: this.element.querySelector(`.${componentName}__small-width`),
            editHeight: this.element.querySelector(`.${componentName}__small-height`),
            iframe: this.element.querySelector('iframe'),
        };

        // Fire Init Funcs
        this.getInitialData();
        this.addEventListeners();
        this.updateIframe();
        this.allowMultipleTids();
    }
}

registerComponent(`.${componentName}`, Iframe);
